/**
 * Target dimensions for a stored photo and its thumbnail (D-081).
 *
 * Pure arithmetic, kept out of the capture pipeline so it can be tested without
 * a canvas. The pipeline in `services/photoPipeline.ts` does the encoding; this
 * decides only how big the result should be.
 *
 * **The aspect ratio is preserved exactly, and an image is never enlarged.**
 * Both properties are load-bearing rather than incidental: `ui-ux-spec.md`
 * forbids reshaping the body, and a resize that changed proportions would do
 * precisely that while looking like a rounding detail. Upscaling a small image
 * would add no detail and would inflate storage for nothing.
 */

/** Longest edge of the stored image, in pixels (D-081). */
export const STORED_MAX_EDGE_PX = 1440

/** Longest edge of the derived grid thumbnail, in pixels (D-081). */
export const THUMBNAIL_MAX_EDGE_PX = 320

/**
 * JPEG quality for the stored image (D-081).
 *
 * JPEG rather than WebP or AVIF: every reader of an exported media ZIP opens a
 * JPEG, which is not true of the newer formats, and the size advantage does not
 * outweigh that for a file the user may hand to someone else.
 */
export const STORED_JPEG_QUALITY = 0.8

/**
 * Thumbnail quality, deliberately lower than the stored image.
 *
 * A thumbnail is derived and disposable — it is never the source of a
 * comparison — so artefacts invisible at 320 px cost nothing.
 */
export const THUMBNAIL_JPEG_QUALITY = 0.7

export const PHOTO_MIME_TYPE = 'image/jpeg'

export interface PixelSize {
  readonly width: number
  readonly height: number
}

/**
 * Scale `source` so its longest edge is at most `maxEdge`.
 *
 * Returns the source size unchanged when it already fits, so a small image is
 * re-encoded (which is what strips its metadata) without being resampled.
 *
 * Dimensions are floored and clamped to at least 1: a canvas of zero width
 * throws, and a 4000 × 3 panorama scaled by the long edge would otherwise
 * round its short edge to zero. That case is absurd as a progress photo and
 * exactly the sort of input that reaches a crash in the field.
 */
export function fitWithin(source: PixelSize, maxEdge: number): PixelSize {
  const longest = Math.max(source.width, source.height)
  if (longest <= maxEdge) {
    return { width: atLeastOne(source.width), height: atLeastOne(source.height) }
  }

  const scale = maxEdge / longest
  return {
    width: atLeastOne(Math.floor(source.width * scale)),
    height: atLeastOne(Math.floor(source.height * scale)),
  }
}

function atLeastOne(value: number): number {
  return Math.max(1, Math.floor(value))
}

/**
 * Rough byte cost of one captured photo — the stored image plus its thumbnail.
 *
 * Used to ask the quota question *before* the camera opens, so a capture that
 * cannot be written is refused while the user can still act on it rather than
 * failing after the shutter (D-081).
 *
 * **A deliberate over-estimate.** At 1440 px and quality 0.8 a real photo lands
 * around 200–350 kB; this returns a figure above the top of that range, because
 * the failure modes are asymmetric. Refusing a capture that would just have fit
 * costs the user one extra tap after they free some space. Accepting one that
 * does not fit costs them the photo, at the one moment that cannot be recreated.
 */
export const ESTIMATED_PHOTO_BYTES = 600 * 1024
